import { useEffect, useState } from 'react';

import { logger } from '@/lib/logger';
import { toErrorInstance } from '@/lib/utils';

export type PolicyType = 'terms' | 'privacy';

interface PolicyData {
  version: number;
  effectiveDate: string;
  content: string;
}

interface UsePolicyReturn {
  /** Policy data including version, effective date, and markdown content */
  data: PolicyData | null;
  /** Whether the initial load is in progress */
  loading: boolean;
  /** Error if any occurred during fetch */
  error: Error | null;
}

// Cache configuration constants
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const REVALIDATE_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour

interface CacheEntry {
  data: PolicyData;
  timestamp: number;
  version: number;
}

function validatePolicyData(data: unknown): data is PolicyData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'version' in data &&
    'effectiveDate' in data &&
    'content' in data &&
    typeof (data as PolicyData).version === 'number' &&
    typeof (data as PolicyData).effectiveDate === 'string' &&
    typeof (data as PolicyData).content === 'string'
  );
}

function validateCacheEntry(entry: unknown): entry is CacheEntry {
  return (
    typeof entry === 'object' &&
    entry !== null &&
    'data' in entry &&
    'timestamp' in entry &&
    'version' in entry &&
    typeof (entry as CacheEntry).timestamp === 'number' &&
    typeof (entry as CacheEntry).version === 'number' &&
    validatePolicyData((entry as CacheEntry).data)
  );
}

/**
 * Hook to fetch policy content from the API.
 *
 * Features:
 * - Automatic caching with localStorage (24hr TTL, 1hr revalidation threshold)
 * - Background revalidation with version detection
 * - AbortController for proper cleanup on unmount
 * - Loading and error state management
 *
 * @param type - The policy type to fetch ('terms' or 'privacy')
 *
 * @example
 * ```tsx
 * const { data, loading, error } = usePolicy('terms');
 *
 * if (loading) return <Skeleton />;
 * if (error) return <ErrorMessage error={error} />;
 * if (data) return <PolicyContent content={data.content} />;
 * ```
 */
export function usePolicy(type: PolicyType): UsePolicyReturn {
  const [data, setData] = useState<PolicyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    async function fetchPolicy() {
      try {
        setLoading(true);
        setError(null);

        // Check localStorage cache first (policies rarely change)
        const cacheKey = `policy_${type}_cache`;
        let cachedEntry: CacheEntry | null = null;

        // localStorage can throw in private browsing or when quota exceeded
        try {
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (validateCacheEntry(parsed)) {
              cachedEntry = parsed;
            } else {
              // Invalid cache structure, remove it
              localStorage.removeItem(cacheKey);
            }
          }
        } catch {
          // localStorage unavailable or invalid JSON, proceed without cache
          try {
            localStorage.removeItem(cacheKey);
          } catch {
            // Ignore
          }
        }

        if (cachedEntry) {
          const cacheAge = Date.now() - cachedEntry.timestamp;

          // Use cached data if less than TTL
          if (cacheAge < CACHE_TTL_MS) {
            if (isMounted) {
              setData(cachedEntry.data);
              setLoading(false);
            }

            // Only revalidate in background if cache is at least threshold old
            // This reduces API load while keeping data relatively fresh
            if (cacheAge > REVALIDATE_THRESHOLD_MS) {
              revalidateInBackground(cachedEntry.version);
            }
            return;
          } else {
            // Cache expired - clear it before fetching fresh
            try {
              localStorage.removeItem(cacheKey);
            } catch {
              // Ignore localStorage errors
            }
          }
        }

        await fetchFromAPI();
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') return;

        const fetchError = toErrorInstance(err);
        logger.error(`[usePolicy] Error fetching ${type} policy`, { error: fetchError });

        if (isMounted) {
          setError(fetchError);
          setLoading(false);
        }
      }
    }

    async function fetchFromAPI() {
      const response = await fetch(`/api/policies/${type}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ${type} policy: ${response.statusText}`);
      }

      const policyData = await response.json();

      // Validate response structure
      if (!validatePolicyData(policyData)) {
        throw new Error(`Invalid ${type} policy data structure received from API`);
      }

      if (isMounted) {
        setData(policyData);
        setLoading(false);
        updateCache(policyData);
      }
    }

    async function revalidateInBackground(cachedVersion: number) {
      try {
        // Bypass browser cache to ensure we detect new versions
        const response = await fetch(`/api/policies/${type}`, {
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          signal: abortController.signal,
        });

        if (!response.ok) return;

        const policyData = await response.json();

        // Validate response structure
        if (!validatePolicyData(policyData)) {
          logger.warn(`[usePolicy] Invalid ${type} policy data from background revalidation`);
          return;
        }

        // Update UI if version changed
        if (isMounted && policyData.version !== cachedVersion) {
          logger.info(`[usePolicy] New ${type} policy version detected`, {
            oldVersion: cachedVersion,
            newVersion: policyData.version,
          });
          setData(policyData);
        }

        // Always update cache to refresh timestamp and repair any issues
        updateCache(policyData);
      } catch (err) {
        // Ignore abort errors, log others for debugging
        if (err instanceof Error && err.name === 'AbortError') return;
        logger.debug(`[usePolicy] Background revalidation failed for ${type}`, { error: err });
      }
    }

    function updateCache(policyData: PolicyData) {
      try {
        const cacheKey = `policy_${type}_cache`;
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            data: policyData,
            timestamp: Date.now(),
            version: policyData.version,
          })
        );
      } catch (err) {
        // QuotaExceededError or other storage errors - app continues without caching
        logger.warn(`[usePolicy] Failed to cache ${type} policy`, { error: err });
      }
    }

    fetchPolicy();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [type]);

  return {
    data,
    loading,
    error,
  };
}
